import RAPIER, {
  type Collider,
  type RigidBody,
  type World,
} from '@dimforge/rapier3d-compat';
import * as THREE from 'three';
import {
  COIN_VARIANT_IDS,
  CoinAssetLibrary,
  type CoinInstanceBatch,
  type CoinInstanceSpec,
  type CoinSeed,
  type QuaternionInput,
  type Vector3Input,
} from '../../assets/coins';
import {
  BonusCardAssetLibrary,
  type BonusCardObject,
} from '../../assets/cards';
import type {
  CardMedalOutcome,
  CardSpawnOptions,
  CoinMedalOutcome,
  CoinSpawnOptions,
  CreateMedalPhysicsSystemOptions,
  DebugForceOutcomeOptions,
  DensePileOptions,
  MedalFixedStepHooks,
  MedalOutcome,
  MedalOutcomeListener,
  MedalPhysicsCounts,
  MedalPhysicsResetOptions,
  MedalPhysicsSystemOptions,
  MedalPhysicsTelemetry,
  MedalPlayfieldBounds,
  PhysicalCard,
  PhysicalCoin,
} from './types';

const DEFAULT_BOUNDS: MedalPlayfieldBounds = Object.freeze({
  deckTopY: 2.18,
  halfWidth: 1.87,
  playfieldBackZ: -0.08,
  frontEdgeZ: 0.9,
  backWallZ: -1.02,
  sideRailFrontZ: 0.68,
  winChuteHalfWidth: 0.76,
  sideLossX: 1.84,
  fallY: 1.42,
});

const DEFAULT_FIXED_TIME_STEP = 1 / 60;
const DEFAULT_MAX_FRAME_DELTA = 0.1;
const DEFAULT_MAX_SUB_STEPS = 6;
const DEFAULT_MAX_COINS = 160;
const DEFAULT_MAX_CARDS = 8;
const DEFAULT_COIN_SCALE = 0.36;
const DEFAULT_CARD_SCALE = 0.38;
const DEFAULT_INITIAL_PILE: DensePileOptions = Object.freeze({
  count: 104,
  seed: 'medal-bed-v1',
  sleeping: true,
});
const EMPTY_OUTCOMES: readonly MedalOutcome[] = Object.freeze([]);
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);

interface CoinRecord extends PhysicalCoin {
  readonly visualSeed: CoinSeed;
  ccdStepsRemaining: number;
}

interface CardRecord extends PhysicalCard {
  readonly visual: BonusCardObject;
}

interface MutableTelemetry {
  engine: 'rapier3d';
  ownsWorld: boolean;
  fixedTimeStep: number;
  accumulatorSeconds: number;
  interpolationAlpha: number;
  simulatedSeconds: number;
  totalFixedSteps: number;
  lastFrameFixedSteps: number;
  clampedFrameSeconds: number;
  droppedAccumulatorSeconds: number;
  coins: number;
  cards: number;
  total: number;
  sleepingCoins: number;
  sleepingCards: number;
  activeCcdCoins: number;
  worldBodies: number;
  worldColliders: number;
  ownedBodies: number;
  ownedColliders: number;
  frontWins: number;
  sideLosses: number;
  queuedOutcomes: number;
  coinVisualBuckets: number;
  estimatedCoinDrawCalls: number;
  coinBatchRebuilds: number;
}

interface ClassifiedExit {
  readonly result: MedalOutcome['result'];
  readonly reason: MedalOutcome['reason'];
}

let rapierInitialization: Promise<void> | undefined;

/** Initializes Rapier exactly once for callers that want to construct their own shared World. */
export function initializeMedalPhysics(): Promise<void> {
  rapierInitialization ??= RAPIER.init();
  return rapierInitialization;
}

/**
 * Creates a system and, when no World is supplied, clearly owns the new World.
 * Use `initializeMedalPhysics()` first when the pusher must be created before this system.
 */
export async function createMedalPhysicsSystem(
  options: CreateMedalPhysicsSystemOptions = {},
): Promise<MedalPhysicsSystem> {
  await initializeMedalPhysics();
  const { world: suppliedWorld, gravity, ...systemOptions } = options;
  const ownsWorld = suppliedWorld === undefined;
  const world = suppliedWorld ?? new RAPIER.World(gravity ?? { x: 0, y: -9.81, z: 0 });
  return new MedalPhysicsSystem({ ...systemOptions, world, ownsWorld });
}

/**
 * Fixed-step Rapier simulation for the medal bed, coins, and bonus cards.
 *
 * Update order per fixed step:
 * `hooks.beforeStep -> world.step -> hooks.afterStep -> classify exits`.
 * This is the only class that steps its World; attach a PusherPlatform through
 * `addStepHooks(createPusherFixedStepHooks(pusher))` instead of stepping twice.
 */
export class MedalPhysicsSystem {
  readonly world: World;
  readonly ownsWorld: boolean;
  readonly root = new THREE.Group();
  readonly bounds: MedalPlayfieldBounds;
  readonly maxCoins: number;
  readonly maxCards: number;
  readonly coinScale: number;
  readonly cardScale: number;
  readonly fixedTimeStep: number;

  private readonly coinAssets: CoinAssetLibrary;
  private readonly cardAssets: BonusCardAssetLibrary;
  private readonly disposeCoinAssets: boolean;
  private readonly disposeCardAssets: boolean;
  private readonly maxFrameDelta: number;
  private readonly maxSubSteps: number;
  private readonly castShadows: boolean;
  private readonly receiveShadows: boolean;
  private readonly boundaryBody: RigidBody;
  private readonly boundaryColliders: Collider[] = [];
  private readonly coins: CoinRecord[] = [];
  private readonly cards: CardRecord[] = [];
  private readonly stepHooks = new Set<MedalFixedStepHooks>();
  private readonly listeners = new Set<MedalOutcomeListener>();
  private readonly outcomeQueue: MedalOutcome[] = [];
  private readonly pendingNotifications: MedalOutcome[] = [];
  private readonly frameOutcomes: MedalOutcome[] = [];
  private readonly mutableTelemetry: MutableTelemetry;
  private readonly initialPile: false | DensePileOptions;
  private coinBatch: CoinInstanceBatch | undefined;
  private coinBatchDirty = true;
  private accumulator = 0;
  private coinSequence = 0;
  private cardSequence = 0;
  private disposed = false;

  constructor(options: MedalPhysicsSystemOptions) {
    this.world = options.world;
    this.ownsWorld = options.ownsWorld ?? false;
    this.maxCoins = readPositiveInteger(options.maxCoins ?? DEFAULT_MAX_COINS, 'maxCoins');
    this.maxCards = readPositiveInteger(options.maxCards ?? DEFAULT_MAX_CARDS, 'maxCards');
    this.coinScale = readPositive(options.coinScale ?? DEFAULT_COIN_SCALE, 'coinScale');
    this.cardScale = readPositive(options.cardScale ?? DEFAULT_CARD_SCALE, 'cardScale');
    this.fixedTimeStep = readPositive(
      options.fixedTimeStep ?? DEFAULT_FIXED_TIME_STEP,
      'fixedTimeStep',
    );
    this.maxFrameDelta = readPositive(
      options.maxFrameDelta ?? DEFAULT_MAX_FRAME_DELTA,
      'maxFrameDelta',
    );
    this.maxSubSteps = readPositiveInteger(
      options.maxSubSteps ?? DEFAULT_MAX_SUB_STEPS,
      'maxSubSteps',
    );
    this.bounds = resolveBounds(options.bounds);
    this.castShadows = options.castShadows ?? true;
    this.receiveShadows = options.receiveShadows ?? true;
    this.coinAssets = options.coinAssets ?? new CoinAssetLibrary(24);
    this.cardAssets = options.cardAssets ?? new BonusCardAssetLibrary();
    this.disposeCoinAssets = options.coinAssets === undefined || (options.disposeProvidedAssets ?? false);
    this.disposeCardAssets = options.cardAssets === undefined || (options.disposeProvidedAssets ?? false);
    this.initialPile = options.initialPile === undefined
      ? DEFAULT_INITIAL_PILE
      : options.initialPile === false
        ? false
        : Object.freeze({ ...options.initialPile });

    this.root.name = 'MedalPhysicsVisuals';
    options.parent?.add(this.root);
    this.world.timestep = this.fixedTimeStep;
    if (options.configureWorld ?? this.ownsWorld) this.configureWorldForStacks();
    this.boundaryBody = this.createBoundaries();

    this.mutableTelemetry = {
      engine: 'rapier3d',
      ownsWorld: this.ownsWorld,
      fixedTimeStep: this.fixedTimeStep,
      accumulatorSeconds: 0,
      interpolationAlpha: 0,
      simulatedSeconds: 0,
      totalFixedSteps: 0,
      lastFrameFixedSteps: 0,
      clampedFrameSeconds: 0,
      droppedAccumulatorSeconds: 0,
      coins: 0,
      cards: 0,
      total: 0,
      sleepingCoins: 0,
      sleepingCards: 0,
      activeCcdCoins: 0,
      worldBodies: this.world.bodies.len(),
      worldColliders: this.world.colliders.len(),
      ownedBodies: 1,
      ownedColliders: this.boundaryColliders.length,
      frontWins: 0,
      sideLosses: 0,
      queuedOutcomes: 0,
      coinVisualBuckets: 0,
      estimatedCoinDrawCalls: 0,
      coinBatchRebuilds: 0,
    };

    for (const hooks of options.stepHooks ?? []) this.stepHooks.add(hooks);
    if (options.onOutcome) this.listeners.add(options.onOutcome);
    if (this.initialPile !== false) this.seedDensePile(this.initialPile);
    this.syncVisuals();
  }

  /** Live telemetry view, updated in place. */
  get telemetry(): MedalPhysicsTelemetry {
    return this.mutableTelemetry;
  }

  /** Live count view backed by telemetry. */
  get counts(): MedalPhysicsCounts {
    return this.mutableTelemetry;
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  addStepHooks(hooks: MedalFixedStepHooks): () => void {
    this.assertActive();
    this.stepHooks.add(hooks);
    return () => this.stepHooks.delete(hooks);
  }

  onOutcome(listener: MedalOutcomeListener): () => void {
    this.assertActive();
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Advances an accumulator at 60 Hz by default, clamps spikes, synchronizes
   * visuals once per rendered frame, and returns outcomes created by this call.
   */
  update(deltaSeconds: number): readonly MedalOutcome[] {
    this.assertActive();
    const requestedDelta = readNonNegative(deltaSeconds, 'deltaSeconds');
    const clampedDelta = Math.min(requestedDelta, this.maxFrameDelta);
    this.mutableTelemetry.clampedFrameSeconds += requestedDelta - clampedDelta;
    this.accumulator += clampedDelta;
    this.frameOutcomes.length = 0;

    let steps = 0;
    while (this.accumulator >= this.fixedTimeStep && steps < this.maxSubSteps) {
      this.simulateFixedStep();
      this.accumulator -= this.fixedTimeStep;
      steps += 1;
    }

    if (this.accumulator >= this.fixedTimeStep) {
      const retained = this.accumulator % this.fixedTimeStep;
      this.mutableTelemetry.droppedAccumulatorSeconds += this.accumulator - retained;
      this.accumulator = retained;
    }

    this.mutableTelemetry.lastFrameFixedSteps = steps;
    this.mutableTelemetry.accumulatorSeconds = this.accumulator;
    this.mutableTelemetry.interpolationAlpha = this.accumulator / this.fixedTimeStep;
    this.syncVisuals();
    this.dispatchPendingOutcomes();
    return this.frameOutcomes.length === 0 ? EMPTY_OUTCOMES : [...this.frameOutcomes];
  }

  /** Deterministic one-step hook for smoke tests and caller-owned fixed loops. */
  stepOnce(): readonly MedalOutcome[] {
    this.assertActive();
    this.frameOutcomes.length = 0;
    this.simulateFixedStep();
    this.mutableTelemetry.lastFrameFixedSteps = 1;
    this.syncVisuals();
    this.dispatchPendingOutcomes();
    return this.frameOutcomes.length === 0 ? EMPTY_OUTCOMES : [...this.frameOutcomes];
  }

  spawnCoin(options: CoinSpawnOptions = {}): PhysicalCoin | null {
    this.assertActive();
    if (this.coins.length >= this.maxCoins) return null;
    const id = options.id ?? `coin-${this.coinSequence + 1}`;
    this.assertUniqueId(id);
    this.coinSequence += 1;
    const source = options.source ?? 'drop';
    const visualSeed = options.seed ?? `${id}:${this.coinSequence}`;
    const metadata = this.coinAssets.createMetadata({
      variant: options.variant,
      finish: options.finish,
      seed: visualSeed,
    });
    const position = options.position
      ? readVector3(options.position)
      : this.createDefaultDropPosition(visualSeed, this.coinSequence);
    const quaternion = options.quaternion
      ? readQuaternion(options.quaternion)
      : new THREE.Quaternion().setFromAxisAngle(Y_AXIS, seededUnit(visualSeed, 13) * Math.PI * 2);
    const ccd = options.ccd
      ?? (source !== 'seed' && position.y > this.bounds.deckTopY + 0.75);
    const ccdSteps = ccd
      ? readPositiveInteger(options.ccdSteps ?? 18, 'ccdSteps')
      : 0;
    const scaledMass = metadata.physics.mass * this.coinScale ** 3;

    const descriptor = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setRotation(quaternion)
      .setLinearDamping(metadata.physics.linearDamping)
      .setAngularDamping(metadata.physics.angularDamping)
      .setCanSleep(true)
      .setSleeping(options.sleeping ?? false)
      .setCcdEnabled(ccd)
      .setUserData({
        kind: 'coin',
        id,
        variant: metadata.variant,
        value: metadata.value,
        source,
      });
    applyInitialVelocity(descriptor, options.linearVelocity, options.angularVelocity);
    const body = this.world.createRigidBody(descriptor);
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cylinder(
        metadata.collision.halfHeight * this.coinScale,
        metadata.collision.radius * this.coinScale,
      )
        .setMass(scaledMass)
        .setFriction(metadata.physics.friction)
        .setRestitution(metadata.physics.restitution),
      body,
    );
    const record: CoinRecord = {
      kind: 'coin',
      id,
      body,
      collider,
      metadata,
      visualSeed,
      ccdStepsRemaining: ccdSteps,
    };
    this.coins.push(record);
    this.coinBatchDirty = true;
    this.refreshTelemetry();
    return record;
  }

  spawnCard(options: CardSpawnOptions = {}): PhysicalCard | null {
    this.assertActive();
    if (this.cards.length >= this.maxCards) return null;
    const id = options.id ?? `card-${this.cardSequence + 1}`;
    this.assertUniqueId(id);
    this.cardSequence += 1;
    const source = options.source ?? 'drop';
    const visual = this.cardAssets.create({
      variantId: options.variantId,
      denomination: options.denomination,
      seed: options.seed ?? `${id}:${this.cardSequence}`,
      instanceId: options.instanceId ?? id,
      castShadow: this.castShadows,
      receiveShadow: this.receiveShadows,
    });
    const metadata = visual.metadata;
    const position = options.position
      ? readVector3(options.position)
      : this.createDefaultDropPosition(options.seed ?? id, this.cardSequence, 0.12);
    const flat = new THREE.Quaternion().setFromAxisAngle(X_AXIS, -Math.PI * 0.5);
    const yaw = new THREE.Quaternion().setFromAxisAngle(
      Y_AXIS,
      options.yaw ?? seededUnit(options.seed ?? id, 29) * Math.PI * 2,
    );
    const quaternion = yaw.multiply(flat);
    const descriptor = RAPIER.RigidBodyDesc.dynamic()
      .setTranslation(position.x, position.y, position.z)
      .setRotation(quaternion)
      .setLinearDamping(metadata.collision.linearDamping)
      .setAngularDamping(metadata.collision.angularDamping)
      .setCanSleep(metadata.collision.canSleep)
      .setSleeping(options.sleeping ?? false)
      .setCcdEnabled(false)
      .setUserData({
        kind: 'bonus-card',
        id,
        variantId: metadata.variantId,
        denomination: metadata.denomination,
        source,
      });
    applyInitialVelocity(descriptor, options.linearVelocity, options.angularVelocity);
    const body = this.world.createRigidBody(descriptor);
    const [halfX, halfY, halfZ] = metadata.collision.halfExtents;
    const collider = this.world.createCollider(
      RAPIER.ColliderDesc.cuboid(
        halfX * this.cardScale,
        halfY * this.cardScale,
        halfZ * this.cardScale,
      )
        .setDensity(metadata.collision.density)
        .setFriction(metadata.collision.friction)
        .setRestitution(metadata.collision.restitution),
      body,
    );
    visual.root.scale.setScalar(this.cardScale);
    visual.root.userData.physicsItemId = id;
    this.root.add(visual.root);
    const record: CardRecord = {
      kind: 'card',
      id,
      body,
      collider,
      object: visual,
      visual,
      metadata,
    };
    this.cards.push(record);
    this.syncCard(record);
    this.refreshTelemetry();
    return record;
  }

  /** Seeds a repeatable, overlap-free stack over the finished machine's medal bed. */
  seedDensePile(options: DensePileOptions = {}): number {
    this.assertActive();
    if (options.clearExistingCoins ?? false) this.clearCoins();
    const requestedCount = Math.max(0, Math.floor(options.count ?? DEFAULT_INITIAL_PILE.count ?? 104));
    const count = Math.min(requestedCount, this.maxCoins - this.coins.length);
    if (count === 0) return 0;
    const seed = options.seed ?? DEFAULT_INITIAL_PILE.seed ?? 'medal-bed-v1';
    const variants = options.variants?.length ? options.variants : COIN_VARIANT_IDS;
    const random = createDeterministicRandom(seed);
    let maximumRadius = 0;
    for (const variant of variants) {
      maximumRadius = Math.max(
        maximumRadius,
        this.coinAssets.createMetadata({ variant }).dimensions.radius * this.coinScale,
      );
    }
    const spacing = maximumRadius * 2 + 0.012;
    const xMin = -this.bounds.halfWidth + maximumRadius + 0.055;
    const xMax = this.bounds.halfWidth - maximumRadius - 0.055;
    const zMin = this.bounds.playfieldBackZ + maximumRadius + 0.055;
    // Real medal games deliberately leave a readable row hanging close to the
    // payout lip. A small safety margin keeps the seeded bodies stable while
    // allowing the first few pusher cycles to create genuine suspense/payouts.
    const frontMargin = Math.max(0.025, options.frontMargin ?? 0.025);
    const zMax = this.bounds.frontEdgeZ - maximumRadius - frontMargin;
    const columns = Math.max(1, Math.floor((xMax - xMin) / spacing) + 1);
    const rows = Math.max(1, Math.floor((zMax - zMin) / spacing) + 1);
    const xStep = columns === 1 ? 0 : (xMax - xMin) / (columns - 1);
    const zStep = rows === 1 ? 0 : (zMax - zMin) / (rows - 1);
    const towers: Array<{ x: number; z: number; height: number }> = [];
    for (let row = 0; row < rows; row += 1) {
      for (let column = 0; column < columns; column += 1) {
        towers.push({
          x: columns === 1 ? 0 : xMin + column * xStep,
          z: rows === 1 ? (zMin + zMax) * 0.5 : zMin + row * zStep,
          height: 0,
        });
      }
    }
    deterministicShuffle(towers, random);

    let spawned = 0;
    for (let index = 0; index < count; index += 1) {
      const tower = towers[index % towers.length];
      const variant = variants[Math.floor(random() * variants.length)] ?? variants[0];
      if (!variant) break;
      const itemSeed = `${String(seed)}:${index}`;
      const metadata = this.coinAssets.createMetadata({ variant, seed: itemSeed });
      const halfHeight = metadata.dimensions.halfThickness * this.coinScale;
      const y = this.bounds.deckTopY + tower.height + halfHeight + 0.002;
      const yaw = random() * Math.PI * 2;
      const coin = this.spawnCoin({
        variant,
        seed: itemSeed,
        position: [tower.x, y, tower.z],
        quaternion: [0, Math.sin(yaw * 0.5), 0, Math.cos(yaw * 0.5)],
        sleeping: options.sleeping ?? true,
        ccd: false,
        source: 'seed',
      });
      if (!coin) break;
      tower.height += metadata.dimensions.thickness * this.coinScale + 0.004;
      spawned += 1;
    }
    this.syncVisuals();
    return spawned;
  }

  debugSpawnCoin(options: CoinSpawnOptions = {}): PhysicalCoin | null {
    const result = this.spawnCoin({ ...options, source: 'debug' });
    this.syncVisuals();
    return result;
  }

  debugSpawnCard(options: CardSpawnOptions = {}): PhysicalCard | null {
    const result = this.spawnCard({ ...options, source: 'debug' });
    this.syncVisuals();
    return result;
  }

  /** Immediately resolves and removes a selected item for deterministic QA. */
  debugForceOutcome(options: DebugForceOutcomeOptions): MedalOutcome | null {
    this.assertActive();
    this.frameOutcomes.length = 0;
    const coinIndex = options.itemKind === 'card'
      ? -1
      : this.findCoinIndex(options.id);
    if (coinIndex >= 0) {
      const outcome = this.removeCoinWithOutcome(
        coinIndex,
        options.result,
        'debug-forced',
      );
      this.syncVisuals();
      this.dispatchPendingOutcomes();
      return outcome;
    }
    const cardIndex = options.itemKind === 'coin'
      ? -1
      : this.findCardIndex(options.id);
    if (cardIndex < 0) return null;
    const outcome = this.removeCardWithOutcome(
      cardIndex,
      options.result,
      'debug-forced',
    );
    this.syncVisuals();
    this.dispatchPendingOutcomes();
    return outcome;
  }

  getItem(id: string): PhysicalCoin | PhysicalCard | undefined {
    return this.coins.find((coin) => coin.id === id)
      ?? this.cards.find((card) => card.id === id);
  }

  drainOutcomes(): MedalOutcome[] {
    this.assertActive();
    const drained = this.outcomeQueue.splice(0);
    this.mutableTelemetry.queuedOutcomes = 0;
    return drained;
  }

  reset(options: MedalPhysicsResetOptions = {}): void {
    this.assertActive();
    this.clearItems();
    this.outcomeQueue.length = 0;
    this.pendingNotifications.length = 0;
    this.frameOutcomes.length = 0;
    this.accumulator = 0;
    this.coinSequence = 0;
    this.cardSequence = 0;
    this.mutableTelemetry.accumulatorSeconds = 0;
    this.mutableTelemetry.interpolationAlpha = 0;
    this.mutableTelemetry.simulatedSeconds = 0;
    this.mutableTelemetry.totalFixedSteps = 0;
    this.mutableTelemetry.lastFrameFixedSteps = 0;
    this.mutableTelemetry.clampedFrameSeconds = 0;
    this.mutableTelemetry.droppedAccumulatorSeconds = 0;
    if (options.clearOutcomeTotals ?? true) {
      this.mutableTelemetry.frontWins = 0;
      this.mutableTelemetry.sideLosses = 0;
    }
    const pile = options.pile === undefined ? this.initialPile : options.pile;
    if (pile !== false) this.seedDensePile(pile);
    this.syncVisuals();
  }

  /** Synchronizes all render transforms from Rapier in one authoritative place. */
  syncVisuals(): void {
    this.assertActive();
    if (this.coinBatchDirty) this.rebuildCoinBatch();
    if (this.coinBatch) {
      for (let index = 0; index < this.coins.length; index += 1) {
        const coin = this.coins[index];
        const translation = coin.body.translation();
        const rotation = coin.body.rotation();
        this.coinBatch.setCoinTransform(index, translation, rotation, this.coinScale);
      }
    }
    for (const card of this.cards) this.syncCard(card);
    this.refreshTelemetry();
  }

  dispose(): void {
    if (this.disposed) return;
    this.clearItems();
    this.coinBatch?.dispose();
    this.coinBatch = undefined;
    this.root.removeFromParent();
    this.root.clear();
    this.listeners.clear();
    this.stepHooks.clear();
    this.outcomeQueue.length = 0;
    this.pendingNotifications.length = 0;
    if (this.world.bodies.contains(this.boundaryBody.handle)) {
      this.world.removeRigidBody(this.boundaryBody);
    }
    if (this.disposeCardAssets) this.cardAssets.dispose();
    if (this.disposeCoinAssets) this.coinAssets.dispose();
    this.disposed = true;
    if (this.ownsWorld) this.world.free();
  }

  private configureWorldForStacks(): void {
    this.world.numSolverIterations = Math.max(this.world.numSolverIterations, 6);
    this.world.numInternalPgsIterations = Math.max(this.world.numInternalPgsIterations, 2);
    this.world.maxCcdSubsteps = Math.max(this.world.maxCcdSubsteps, 1);
  }

  private createBoundaries(): RigidBody {
    const body = this.world.createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setUserData({ kind: 'medal-playfield-boundaries' }),
    );
    const floorHalfDepth = (this.bounds.frontEdgeZ - this.bounds.playfieldBackZ) * 0.5;
    const floorCenterZ = (this.bounds.frontEdgeZ + this.bounds.playfieldBackZ) * 0.5;
    this.addBoundaryCollider(
      body,
      RAPIER.ColliderDesc.cuboid(this.bounds.halfWidth, 0.08, floorHalfDepth)
        .setTranslation(0, this.bounds.deckTopY - 0.08, floorCenterZ),
    );
    this.addBoundaryCollider(
      body,
      RAPIER.ColliderDesc.cuboid(this.bounds.halfWidth + 0.05, 0.34, 0.055)
        .setTranslation(0, this.bounds.deckTopY + 0.34, this.bounds.backWallZ),
    );

    const railBack = this.bounds.backWallZ + 0.055;
    const railHalfDepth = (this.bounds.sideRailFrontZ - railBack) * 0.5;
    const railCenterZ = (this.bounds.sideRailFrontZ + railBack) * 0.5;
    for (const side of [-1, 1]) {
      this.addBoundaryCollider(
        body,
        RAPIER.ColliderDesc.cuboid(0.055, 0.22, railHalfDepth)
          .setTranslation(
            side * (this.bounds.halfWidth + 0.025),
            this.bounds.deckTopY + 0.22,
            railCenterZ,
          ),
      );
    }

    const guardHalfWidth = (this.bounds.halfWidth - this.bounds.winChuteHalfWidth) * 0.5;
    const guardCenterX = this.bounds.winChuteHalfWidth + guardHalfWidth;
    for (const side of [-1, 1]) {
      this.addBoundaryCollider(
        body,
        RAPIER.ColliderDesc.cuboid(guardHalfWidth, 0.15, 0.055)
          .setTranslation(
            side * guardCenterX,
            this.bounds.deckTopY + 0.15,
            this.bounds.frontEdgeZ + 0.025,
          ),
      );
    }
    return body;
  }

  private addBoundaryCollider(body: RigidBody, descriptor: RAPIER.ColliderDesc): void {
    descriptor.setFriction(0.72).setRestitution(0.025);
    this.boundaryColliders.push(this.world.createCollider(descriptor, body));
  }

  private simulateFixedStep(): void {
    this.world.timestep = this.fixedTimeStep;
    for (const hooks of this.stepHooks) hooks.beforeStep?.(this.fixedTimeStep, this.world);
    this.world.step();
    for (const hooks of this.stepHooks) hooks.afterStep?.(this.fixedTimeStep, this.world);
    this.mutableTelemetry.totalFixedSteps += 1;
    this.mutableTelemetry.simulatedSeconds += this.fixedTimeStep;
    this.expireCoinCcd();
    this.classifyFallenItems();
  }

  private expireCoinCcd(): void {
    for (const coin of this.coins) {
      if (coin.ccdStepsRemaining <= 0) continue;
      coin.ccdStepsRemaining -= 1;
      if (coin.ccdStepsRemaining === 0) coin.body.enableCcd(false);
    }
  }

  private classifyFallenItems(): void {
    for (let index = this.coins.length - 1; index >= 0; index -= 1) {
      const exit = this.classifyPosition(this.coins[index].body.translation());
      if (exit) this.removeCoinWithOutcome(index, exit.result, exit.reason);
    }
    for (let index = this.cards.length - 1; index >= 0; index -= 1) {
      const exit = this.classifyPosition(this.cards[index].body.translation());
      if (exit) this.removeCardWithOutcome(index, exit.result, exit.reason);
    }
  }

  private classifyPosition(position: Readonly<{ x: number; y: number; z: number }>): ClassifiedExit | null {
    if (position.y > this.bounds.fallY) return null;
    if (
      position.z >= this.bounds.frontEdgeZ - 0.08
      && Math.abs(position.x) <= this.bounds.winChuteHalfWidth
    ) {
      return { result: 'front-win', reason: 'front-chute' };
    }
    if (position.x <= -this.bounds.sideLossX) {
      return { result: 'side-loss', reason: 'left-gutter' };
    }
    if (position.x >= this.bounds.sideLossX) {
      return { result: 'side-loss', reason: 'right-gutter' };
    }
    if (position.z >= this.bounds.frontEdgeZ - 0.08) {
      return { result: 'side-loss', reason: 'front-miss' };
    }
    if (position.z <= this.bounds.backWallZ - 0.15) {
      return { result: 'side-loss', reason: 'back-overflow' };
    }
    return { result: 'side-loss', reason: 'below-machine' };
  }

  private removeCoinWithOutcome(
    index: number,
    result: MedalOutcome['result'],
    reason: MedalOutcome['reason'],
  ): CoinMedalOutcome {
    const coin = this.coins[index];
    const position = coin.body.translation();
    const outcome: CoinMedalOutcome = Object.freeze({
      itemKind: 'coin',
      result,
      id: coin.id,
      fixedStep: this.mutableTelemetry.totalFixedSteps,
      simulatedSeconds: this.mutableTelemetry.simulatedSeconds,
      position: Object.freeze({ x: position.x, y: position.y, z: position.z }),
      reason,
      variant: coin.metadata.variant,
      finish: coin.metadata.finish,
      value: coin.metadata.value,
    });
    this.coins.splice(index, 1);
    if (this.world.bodies.contains(coin.body.handle)) this.world.removeRigidBody(coin.body);
    this.coinBatchDirty = true;
    this.recordOutcome(outcome);
    return outcome;
  }

  private removeCardWithOutcome(
    index: number,
    result: MedalOutcome['result'],
    reason: MedalOutcome['reason'],
  ): CardMedalOutcome {
    const card = this.cards[index];
    const position = card.body.translation();
    const outcome: CardMedalOutcome = Object.freeze({
      itemKind: 'card',
      result,
      id: card.id,
      fixedStep: this.mutableTelemetry.totalFixedSteps,
      simulatedSeconds: this.mutableTelemetry.simulatedSeconds,
      position: Object.freeze({ x: position.x, y: position.y, z: position.z }),
      reason,
      variantId: card.metadata.variantId,
      denomination: card.metadata.denomination,
      rewardUnit: card.metadata.rewardUnit,
    });
    this.cards.splice(index, 1);
    card.visual.dispose();
    if (this.world.bodies.contains(card.body.handle)) this.world.removeRigidBody(card.body);
    this.recordOutcome(outcome);
    return outcome;
  }

  private recordOutcome(outcome: MedalOutcome): void {
    this.outcomeQueue.push(outcome);
    this.frameOutcomes.push(outcome);
    this.pendingNotifications.push(outcome);
    if (outcome.result === 'front-win') this.mutableTelemetry.frontWins += 1;
    else this.mutableTelemetry.sideLosses += 1;
  }

  private dispatchPendingOutcomes(): void {
    if (this.pendingNotifications.length === 0) return;
    const outcomes = this.pendingNotifications.splice(0);
    for (const outcome of outcomes) {
      for (const listener of [...this.listeners]) listener(outcome);
    }
    this.refreshTelemetry();
  }

  private rebuildCoinBatch(): void {
    this.coinBatch?.dispose();
    this.coinBatch = undefined;
    if (this.coins.length > 0) {
      const entries: CoinInstanceSpec[] = this.coins.map((coin) => {
        const translation = coin.body.translation();
        const rotation = coin.body.rotation();
        return {
          id: coin.id,
          variant: coin.metadata.variant,
          finish: coin.metadata.finish,
          seed: coin.visualSeed,
          position: [translation.x, translation.y, translation.z],
          quaternion: [rotation.x, rotation.y, rotation.z, rotation.w],
          uniformScale: this.coinScale,
        };
      });
      const batch = this.coinAssets.createInstancedCoins(entries, {
        name: 'Physical Medal Coins',
        castShadow: this.castShadows,
        receiveShadow: this.receiveShadows,
      });
      batch.traverse((object) => {
        if (object instanceof THREE.InstancedMesh) object.frustumCulled = false;
      });
      this.root.add(batch);
      this.coinBatch = batch;
    }
    this.coinBatchDirty = false;
    this.mutableTelemetry.coinBatchRebuilds += 1;
  }

  private syncCard(card: CardRecord): void {
    const translation = card.body.translation();
    const rotation = card.body.rotation();
    card.visual.root.position.set(translation.x, translation.y, translation.z);
    card.visual.root.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
  }

  private clearItems(): void {
    this.clearCoins();
    for (const card of this.cards) {
      card.visual.dispose();
      if (this.world.bodies.contains(card.body.handle)) this.world.removeRigidBody(card.body);
    }
    this.cards.length = 0;
    this.refreshTelemetry();
  }

  private clearCoins(): void {
    for (const coin of this.coins) {
      if (this.world.bodies.contains(coin.body.handle)) this.world.removeRigidBody(coin.body);
    }
    this.coins.length = 0;
    this.coinBatchDirty = true;
  }

  private createDefaultDropPosition(seed: CoinSeed, sequence: number, zOffset = 0): THREE.Vector3 {
    const halfDropWidth = Math.min(this.bounds.halfWidth - 0.25, 1.46);
    const x = (seededUnit(seed, sequence * 17 + 3) * 2 - 1) * halfDropWidth;
    const z = this.bounds.playfieldBackZ + 0.23 + zOffset;
    return new THREE.Vector3(x, this.bounds.deckTopY + 1.38, z);
  }

  private findCoinIndex(id: string | undefined): number {
    if (this.coins.length === 0) return -1;
    return id === undefined ? 0 : this.coins.findIndex((coin) => coin.id === id);
  }

  private findCardIndex(id: string | undefined): number {
    if (this.cards.length === 0) return -1;
    return id === undefined ? 0 : this.cards.findIndex((card) => card.id === id);
  }

  private assertUniqueId(id: string): void {
    if (this.getItem(id)) throw new Error(`A physical medal item with id "${id}" already exists.`);
  }

  private refreshTelemetry(): void {
    let sleepingCoins = 0;
    let activeCcdCoins = 0;
    const visualKeys = new Set<string>();
    for (const coin of this.coins) {
      if (coin.body.isSleeping()) sleepingCoins += 1;
      if (coin.ccdStepsRemaining > 0 && coin.body.isCcdEnabled()) activeCcdCoins += 1;
      visualKeys.add(coin.metadata.instancingKey);
    }
    let sleepingCards = 0;
    for (const card of this.cards) {
      if (card.body.isSleeping()) sleepingCards += 1;
    }
    this.mutableTelemetry.coins = this.coins.length;
    this.mutableTelemetry.cards = this.cards.length;
    this.mutableTelemetry.total = this.coins.length + this.cards.length;
    this.mutableTelemetry.sleepingCoins = sleepingCoins;
    this.mutableTelemetry.sleepingCards = sleepingCards;
    this.mutableTelemetry.activeCcdCoins = activeCcdCoins;
    this.mutableTelemetry.worldBodies = this.world.bodies.len();
    this.mutableTelemetry.worldColliders = this.world.colliders.len();
    this.mutableTelemetry.ownedBodies = this.coins.length + this.cards.length + 1;
    this.mutableTelemetry.ownedColliders = this.boundaryColliders.length + this.coins.length + this.cards.length;
    this.mutableTelemetry.queuedOutcomes = this.outcomeQueue.length;
    this.mutableTelemetry.coinVisualBuckets = visualKeys.size;
    this.mutableTelemetry.estimatedCoinDrawCalls = visualKeys.size * 3;
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('MedalPhysicsSystem has been disposed.');
  }
}

/** Structural adapter for PusherPlatform without coupling the physics core to its class. */
export function createPusherFixedStepHooks(pusher: {
  fixedStep(fixedDeltaSeconds: number): unknown;
  afterPhysicsStep(): unknown;
}): MedalFixedStepHooks {
  return Object.freeze({
    beforeStep: (fixedDeltaSeconds: number) => {
      pusher.fixedStep(fixedDeltaSeconds);
    },
    afterStep: () => {
      pusher.afterPhysicsStep();
    },
  });
}

function resolveBounds(overrides: Partial<MedalPlayfieldBounds> | undefined): MedalPlayfieldBounds {
  const result: MedalPlayfieldBounds = Object.freeze({
    deckTopY: readFinite(overrides?.deckTopY ?? DEFAULT_BOUNDS.deckTopY, 'bounds.deckTopY'),
    halfWidth: readPositive(overrides?.halfWidth ?? DEFAULT_BOUNDS.halfWidth, 'bounds.halfWidth'),
    playfieldBackZ: readFinite(
      overrides?.playfieldBackZ ?? DEFAULT_BOUNDS.playfieldBackZ,
      'bounds.playfieldBackZ',
    ),
    frontEdgeZ: readFinite(overrides?.frontEdgeZ ?? DEFAULT_BOUNDS.frontEdgeZ, 'bounds.frontEdgeZ'),
    backWallZ: readFinite(overrides?.backWallZ ?? DEFAULT_BOUNDS.backWallZ, 'bounds.backWallZ'),
    sideRailFrontZ: readFinite(
      overrides?.sideRailFrontZ ?? DEFAULT_BOUNDS.sideRailFrontZ,
      'bounds.sideRailFrontZ',
    ),
    winChuteHalfWidth: readPositive(
      overrides?.winChuteHalfWidth ?? DEFAULT_BOUNDS.winChuteHalfWidth,
      'bounds.winChuteHalfWidth',
    ),
    sideLossX: readPositive(overrides?.sideLossX ?? DEFAULT_BOUNDS.sideLossX, 'bounds.sideLossX'),
    fallY: readFinite(overrides?.fallY ?? DEFAULT_BOUNDS.fallY, 'bounds.fallY'),
  });
  if (result.frontEdgeZ <= result.playfieldBackZ) {
    throw new Error('bounds.frontEdgeZ must be greater than bounds.playfieldBackZ.');
  }
  if (result.backWallZ >= result.playfieldBackZ) {
    throw new Error('bounds.backWallZ must be behind bounds.playfieldBackZ.');
  }
  if (result.sideRailFrontZ <= result.backWallZ || result.sideRailFrontZ >= result.frontEdgeZ) {
    throw new Error('bounds.sideRailFrontZ must lie between the back wall and front edge.');
  }
  if (result.winChuteHalfWidth >= result.halfWidth) {
    throw new Error('bounds.winChuteHalfWidth must be smaller than bounds.halfWidth.');
  }
  return result;
}

function applyInitialVelocity(
  descriptor: RAPIER.RigidBodyDesc,
  linearVelocity: Vector3Input | undefined,
  angularVelocity: Vector3Input | undefined,
): void {
  if (linearVelocity) {
    const velocity = readVector3(linearVelocity);
    descriptor.setLinvel(velocity.x, velocity.y, velocity.z);
  }
  if (angularVelocity) descriptor.setAngvel(readVector3(angularVelocity));
}

function readVector3(input: Vector3Input): THREE.Vector3 {
  if (Array.isArray(input)) return new THREE.Vector3(input[0], input[1], input[2]);
  const value = input as Readonly<{ x: number; y: number; z: number }>;
  return new THREE.Vector3(value.x, value.y, value.z);
}

function readQuaternion(input: QuaternionInput): THREE.Quaternion {
  const result = Array.isArray(input)
    ? new THREE.Quaternion(input[0], input[1], input[2], input[3])
    : new THREE.Quaternion(
        (input as Readonly<{ x: number }>).x,
        (input as Readonly<{ y: number }>).y,
        (input as Readonly<{ z: number }>).z,
        (input as Readonly<{ w: number }>).w,
      );
  if (result.lengthSq() <= Number.EPSILON) return result.identity();
  return result.normalize();
}

function createDeterministicRandom(seed: CoinSeed): () => number {
  let state = hashSeed(seed) || 0x6d2b79f5;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
  };
}

function deterministicShuffle<T>(values: T[], random: () => number): void {
  for (let index = values.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    const value = values[index];
    values[index] = values[swapIndex];
    values[swapIndex] = value;
  }
}

function seededUnit(seed: CoinSeed, salt: number): number {
  return hashSeed(`${String(seed)}:${salt}`) / 4_294_967_296;
}

function hashSeed(seed: CoinSeed): number {
  if (typeof seed === 'number') {
    if (!Number.isFinite(seed)) return 0;
    let value = Math.trunc(seed) >>> 0;
    value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
    value = Math.imul(value ^ (value >>> 16), 0x45d9f3b);
    return (value ^ (value >>> 16)) >>> 0;
  }
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function readFinite(value: number, name: string): number {
  if (!Number.isFinite(value)) throw new Error(`${name} must be finite.`);
  return value;
}

function readPositive(value: number, name: string): number {
  const result = readFinite(value, name);
  if (result <= 0) throw new Error(`${name} must be greater than zero.`);
  return result;
}

function readNonNegative(value: number, name: string): number {
  const result = readFinite(value, name);
  if (result < 0) throw new Error(`${name} must be non-negative.`);
  return result;
}

function readPositiveInteger(value: number, name: string): number {
  if (!Number.isInteger(value) || value <= 0) {
    throw new Error(`${name} must be a positive integer.`);
  }
  return value;
}
